import type { ClientToolName } from "@salon/types/ai-agent";
import { create } from "zustand";
import type { ClientToolReference } from "../api/ai-agent-client";

interface ToolImplementation {
  execute: (args: Record<string, any>) => Promise<any> | any;
}

interface ClientToolsStore {
  toolReferences: Map<string, ClientToolReference>;
  toolImplementations: Map<string, ToolImplementation>;

  /**
   * Register a client tool
   */
  registerTool: (
    name: ClientToolName,
    description: string,
    parameters: Record<string, any>,
    execute: (args: Record<string, any>) => Promise<any> | any
  ) => void;

  /**
   * Unregister a client tool (for cleanup on unmount)
   */
  unregisterTool: (name: string) => void;

  /**
   * Get all tool references for sending to backend
   */
  getToolReferences: () => ClientToolReference[];

  /**
   * Get a specific tool implementation for execution
   */
  getToolImplementation: (name: string) => ToolImplementation | undefined;

  /**
   * Get all tool implementations
   */
  getAllToolImplementations: () => Map<string, ToolImplementation>;

  /**
   * Clear all tools (for testing/cleanup)
   */
  clearTools: () => void;
}

export const useClientToolsStore = create<ClientToolsStore>((set, get) => ({
  toolReferences: new Map(),
  toolImplementations: new Map(),

  registerTool: (name, _description, _parameters, execute) => {
    set((state) => {
      const newToolReferences = new Map(state.toolReferences);
      const newToolImplementations = new Map(state.toolImplementations);

      newToolReferences.set(name, { name });
      newToolImplementations.set(name, { execute });

      return {
        toolReferences: newToolReferences,
        toolImplementations: newToolImplementations,
      };
    });
  },

  unregisterTool: (name) => {
    set((state) => {
      const newToolReferences = new Map(state.toolReferences);
      const newToolImplementations = new Map(state.toolImplementations);

      newToolReferences.delete(name);
      newToolImplementations.delete(name);

      return {
        toolReferences: newToolReferences,
        toolImplementations: newToolImplementations,
      };
    });
  },

  getToolReferences: () => {
    const refs = Array.from(get().toolReferences.values());
    return refs;
  },

  getToolImplementation: (name) => {
    return get().toolImplementations.get(name);
  },

  getAllToolImplementations: () => {
    return get().toolImplementations;
  },

  clearTools: () => {
    set({ toolReferences: new Map(), toolImplementations: new Map() });
  },
}));
